import "@/styles/escaperoom/menu.scss";

import { useUser } from "@auth0/nextjs-auth0/client";
import { Box, Button, Image, Tooltip } from "@mantine/core";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import MenuDialog from "@/components/general/menu-dialog/menu-dialog";
import { SectionTitle } from "@/components/general/section-title/section-title";
import SoundSlider from "@/components/general/sound-slider/sound-slider";
import { useStateContext } from "@/context/context-provider";
import { useSocketContext } from "@/context/socket-context-provider";

const Menu = ({ escaperoomHints, roomUrl, title }) => {
    const { t } = useTranslation();
    const {
        menu,
        setMenu,
        setInPointerLock,
        items,
        solved,
        hideTimer,
        setHideTimer,
        setEndScreenOpen,
        inspectedItem,
        graphicsQuality,
        setGraphicsQuality,
    } = useStateContext();
    const { lobbyId, createLobby } = useSocketContext();

    const [hints, setHints] = useState(escaperoomHints);
    const { user: session } = useUser();

    const [currentRiddle, setCurrentRiddle] = useState("");
    const router = useRouter();

    const [newName, setNewName] = useState(null);
    const [newColor, setNewColor] = useState(null);

    useEffect(() => {
        const name = localStorage.getItem("playerName");
        if (name) {
            setNewName(name);
        }
        const color = localStorage.getItem("playerColor");
        if (color) {
            setNewColor(color);
        }
    }, []);

    const toggleHideTimer = () => {
        const nextState = !hideTimer;
        setHideTimer(nextState);
        localStorage.setItem("hideTimer", JSON.stringify(nextState));
    };

    const updateFirstUnsolvedHint = (categoryName) => {
        if (solved && !solved[categoryName]?.isSolved) {
            const updatedHints = { ...hints };
            const category = updatedHints[categoryName];
            const firstUnsolvedHint = category?.find((hint) => !hint.displayed);

            if (firstUnsolvedHint) {
                firstUnsolvedHint.displayed = true;
                setHints(updatedHints);
            }
        }
    };

    // Lobby erstellen und Einzelspieler-Fortschritt mitgeben
    const openLobby = () => {
        if (!lobbyId && session) {
            const currentUser = { name: newName, color: newColor };
            createLobby(currentUser, { items, puzzles: solved }, (cb) => {
                navigator.clipboard.writeText("https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + cb.lobbyId);
                router.push(`?lobby=${cb.lobbyId}`);
            });
        } else if (lobbyId) {
            navigator.clipboard.writeText("https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + lobbyId);
            router.push(`?lobby=${lobbyId}`);
        }
    };

    const toggleGraphicsQuality = () => {
        const modes = ["high", "medium", "low"];
        const currentIndex = modes.indexOf(graphicsQuality || "high");
        const nextMode = modes[(currentIndex + 1) % modes.length];

        setGraphicsQuality(nextMode);
        localStorage.setItem("graphicsQuality", nextMode);
    };

    const getGraphicsButtonText = () => {
        switch (graphicsQuality) {
            case "medium": {
                return "Grafik: Ausgewogen";
            }
            case "low": {
                return "Grafik: Performance (Max FPS)";
            }
            default: {
                return "Grafik: Hoch";
            }
        }
    };

    return (
        menu && (
            <div className="menu-contianer">
                <Box className="menu">
                    <SectionTitle title={title} subTitle="Online Escape Room" />

                    {/* Continue */}
                    <Button
                        lts=".06rem"
                        size="lg"
                        variant="outline"
                        color="gray"
                        radius="md"
                        onClick={() => {
                            setMenu(false);
                            if (!inspectedItem) {
                                setInPointerLock(true);
                            }
                        }}>
                        {t("menu.continueButton")}
                    </Button>

                    {/* Lobby */}
                    <Tooltip
                        disabled={!!session}
                        label={
                            lobbyId
                                ? `Dein Einladungslink: ${"https://online-escaperoom.de" + "/details/" + roomUrl + "?lobby=" + lobbyId}`
                                : session
                                  ? ""
                                  : "Du musst eingeloggt sein, um eine Lobby zu erstellen"
                        }>
                        <Button
                            lts=".06rem"
                            size="lg"
                            variant="outline"
                            color="gray"
                            radius="md"
                            disabled={!(lobbyId || session)}
                            onClick={openLobby}>
                            {lobbyId ? "Kopiere Einladungslink" : "Öffne Multiplayer Lobby"}
                        </Button>
                    </Tooltip>

                    {/* Controls */}
                    <MenuDialog title={t("menu.contollsButton")}>
                        <div className="controlls-container">
                            <br />
                            <div className="controll">
                                <p className="key">{t("menu.controlls.lc.name")}</p>
                                <p className="description">{t("menu.controlls.lc.description")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">{t("menu.controlls.rc.name")}</p>
                                <p className="description">{t("menu.controlls.rc.description")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">{t("menu.controlls.mw.name")}</p>
                                <p className="description">{t("menu.controlls.mw.description")}</p>
                            </div>
                            <br />
                            <div className="controll">
                                <p className="key">W</p>
                                <p className="description">{t("menu.controlls.w")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">A</p>
                                <p className="description">{t("menu.controlls.a")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">S</p>
                                <p className="description">{t("menu.controlls.s")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">D</p>
                                <p className="description">{t("menu.controlls.d")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">{t("menu.controlls.space.name")}</p>
                                <p className="description">{t("menu.controlls.space.description")}</p>
                            </div>
                            <br />
                            <div className="controll">
                                <p className="key">E, F, I </p>
                                <p className="description">{t("menu.controlls.f")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">Q</p>
                                <p className="description">{t("menu.controlls.q")}</p>
                            </div>
                            <div className="controll">
                                <p className="key">Z, C</p>
                                <p className="description">{t("menu.controlls.z")}</p>
                            </div>
                        </div>
                    </MenuDialog>

                    {/* Sound */}
                    <MenuDialog title={t("menu.soundButton")}>
                        <label className="description">{t("menu.volume")}</label>
                        <SoundSlider />
                        <label className="description">{t("menu.backgroundMusic")}</label>
                        <SoundSlider soundSetting="background" />
                    </MenuDialog>

                    {/* Timer ein-/ausblenden */}
                    <Button lts=".06rem" size="lg" variant="outline" color="gray" radius="md" onClick={toggleHideTimer}>
                        {hideTimer ? t("menu.showTimer") : t("menu.hideTimer")}
                    </Button>
                    {roomUrl === "island" && (
                        <Button lts=".06rem" size="lg" variant="outline" color="gray" radius="md" onClick={toggleGraphicsQuality}>
                            {getGraphicsButtonText()}
                        </Button>
                    )}

                    {/* Hints */}
                    <MenuDialog
                        title={t("menu.hintButton")}
                        onClick={() => {
                            const firstUnsolvedRiddle = solved ? Object.keys(solved).find((riddle) => !solved[riddle]?.isSolved) : null;
                            if (firstUnsolvedRiddle) {
                                setCurrentRiddle(firstUnsolvedRiddle);
                            } else {
                                setCurrentRiddle("allSolved");
                            }
                        }}>
                        <div className="hint-container">
                            {hints &&
                                hints[currentRiddle] &&
                                hints[currentRiddle].length > 0 &&
                                hints[currentRiddle].map((hint) =>
                                    hint.displayed ? (
                                        <React.Fragment key={uuid()}>
                                            <p className="hint-item">{hint.text}</p>
                                            {hint.image && <Image bdrs="md" src={hint.image} alt="Hint" className="hint-image" />}
                                        </React.Fragment>
                                    ) : null
                                )}
                        </div>
                        <Button
                            lts=".06rem"
                            mt="md"
                            size="lg"
                            variant="outline"
                            color="gray"
                            radius="md"
                            className="show-hint-button"
                            onClick={() => {
                                updateFirstUnsolvedHint(currentRiddle);
                            }}>
                            {t("menu.showHintButton")}
                        </Button>
                    </MenuDialog>

                    {/* Restart (nur im Singleplayer) */}
                    {!lobbyId && (
                        <Button
                            lts=".06rem"
                            size="lg"
                            variant="outline"
                            color="gray"
                            radius="md"
                            className="menu-button"
                            onClick={() => {
                                localStorage.removeItem(roomUrl);
                                globalThis.location.reload();
                            }}>
                            {t("menu.restartButton")}
                        </Button>
                    )}

                    {/* Back to Menu */}
                    <Button
                        lts=".06rem"
                        size="lg"
                        variant="outline"
                        color="gray"
                        radius="md"
                        className="menu-button"
                        onClick={() => {
                            setMenu(false);
                            setInPointerLock(false);
                            setEndScreenOpen(false);
                            router.push("/details/" + roomUrl);
                        }}>
                        {t("menu.menuButton")}
                    </Button>
                </Box>
            </div>
        )
    );
};

export default Menu;
